import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { getSessionStorage, setSessionStorage } from 'src/functions/localStorageHandler';

const MEMBER_SLIDER_OPTIONS = [50, 100, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000];

interface CalculatorControllerProps {
  initialSliderIndexValue?: number;
  initialSubscriptionValue?: number;
  initialCourseValue?: number;
  initialProInvestmentValue?: number;
  eventLabel?: string;
  annual?: boolean;
}

interface CalculatorControllerReturn {
  sliderIndexValue: number;
  subscriptionValue: number;
  courseValue: number;
  memberNumber: string;
  annualRevenue: string | null;
  profitValue: string;
  roiValue: number;
  setSliderIndexValue: Dispatch<SetStateAction<number>>;
  setSubscriptionValue: Dispatch<SetStateAction<number>>;
  setCourseValue: Dispatch<SetStateAction<number>>;
  setProInvestmentValue: Dispatch<SetStateAction<number>>;
}

const useCalculatorController = (props?: CalculatorControllerProps): CalculatorControllerReturn => {
  const {
    initialSliderIndexValue = 0,
    initialSubscriptionValue = 48,
    initialCourseValue = 500,
    initialProInvestmentValue = 0,
    eventLabel,
    annual = true,
  } = props || {};

  const [sliderIndexValue, setSliderIndexValue] = useState(initialSliderIndexValue);
  const [subscriptionValue, setSubscriptionValue] = useState(initialSubscriptionValue);
  const [courseValue, setCourseValue] = useState(initialCourseValue);
  const [proInvestmentValue, setProInvestmentValue] = useState(initialProInvestmentValue);

  useEffect(() => {
    if (!eventLabel) return;

    const eventName = eventLabel.toLowerCase().replace(/ /g, '_');
    const hasInteracted = getSessionStorage(eventName);

    if (hasInteracted) return;

    const valuesAreDefault =
      sliderIndexValue === initialSliderIndexValue &&
      subscriptionValue === initialSubscriptionValue &&
      courseValue === initialCourseValue &&
      proInvestmentValue === initialProInvestmentValue;

    if (!valuesAreDefault) {
      if ((window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: eventName,
          label: eventLabel,
        });
      }

      setSessionStorage(eventName, 'true');
    }
  }, [
    sliderIndexValue,
    subscriptionValue,
    courseValue,
    proInvestmentValue,
    eventLabel,
    initialSliderIndexValue,
    initialSubscriptionValue,
    initialCourseValue,
    initialProInvestmentValue,
  ]);

  const memberNumber = MEMBER_SLIDER_OPTIONS[sliderIndexValue];

  const formatThousandComma = (value: number) => {
    // Regex to capture sequence of 3 digits, to split them with comma
    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const calculateAnnualRevenue = () => {
    const subscriptionMultiplier = annual ? 12 : 1;

    const revenue = memberNumber * (subscriptionValue * subscriptionMultiplier + courseValue);
    // Aleatory Max value provided by design
    if (revenue > 999999999) {
      return null;
    }
    return revenue;
  };

  const annualRevenue = calculateAnnualRevenue();
  const profitValue = annualRevenue - proInvestmentValue;
  const roiValue = Math.round((profitValue / proInvestmentValue) * 10) / 10;

  return {
    sliderIndexValue,
    subscriptionValue,
    courseValue,
    memberNumber: formatThousandComma(memberNumber),
    annualRevenue: annualRevenue ? formatThousandComma(annualRevenue) : null,
    profitValue: formatThousandComma(profitValue),
    roiValue,
    setSliderIndexValue,
    setSubscriptionValue,
    setCourseValue,
    setProInvestmentValue,
  };
};

export default useCalculatorController;
